import { alpha, type Components, type Theme } from '@mui/material/styles'

import { borderRadius, spacing } from '@/themes/tokens'

// Extend Paper to accept custom variants
declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    compact: true
    spacious: true
    elevated: true
    form: true
    orgCard: true
    tierCard: true
    detailCard: true
    expandableCard: true
    selectMenu: true
    actionMenu: true
    infoRow: true
    infoRowStacked: true
  }
}

export const MuiPaperOverride: Components<Theme>['MuiPaper'] = {
  defaultProps: {
    variant: 'elevation',
    elevation: 0,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      padding: theme.spacing(spacing.lg),
      borderRadius: theme.spacing(spacing.md),
    }),
    outlined: ({ theme }) => ({
      border: `${theme.spacing(spacing.sm)} solid ${theme.palette.divider}`,
    }),
  },
  variants: [
    {
      props: { variant: 'compact' },
      style: ({ theme }) => ({
        padding: theme.spacing(spacing.md),
        borderRadius: theme.spacing(spacing.sm),
      }),
    },
    {
      props: { variant: 'spacious' },
      style: ({ theme }) => ({
        padding: theme.spacing(spacing.xl),
        borderRadius: theme.spacing(spacing.lg),
      }),
    },
    {
      props: { variant: 'elevated' },
      style: ({ theme }) => ({
        padding: theme.spacing(spacing.lg),
        borderRadius: theme.spacing(spacing.md),
        boxShadow: theme.shadows[3],
      }),
    },
    {
      props: { variant: 'form' },
      style: ({ theme }) => ({
        padding: theme.spacing(spacing.xl),
        borderRadius: 12,
        boxShadow: theme.shadows[1],
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 400,
        gap: theme.spacing(spacing.md),
      }),
    },
    {
      props: { variant: 'infoRow' },
      style: ({ theme }) => ({
        padding: theme.spacing(0, 2.5),
        minHeight: 52,
        display: 'flex',
        alignItems: 'center',
        borderRadius: theme.spacing(spacing.sm),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }),
    },
    {
      // The same row with the value under the label instead of opposite it, for
      // columns too narrow to hold both on one line. Fills its grid cell's height
      // so a row of them lines up however long each value runs.
      props: { variant: 'infoRowStacked' },
      style: ({ theme }) => ({
        padding: theme.spacing(spacing.md),
        borderRadius: theme.spacing(spacing.sm),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        height: '100%',
        boxSizing: 'border-box',
      }),
    },
    {
      // A static, non-interactive card. Fills its grid cell's height so cards
      // sitting side by side line up regardless of how much each one holds.
      props: { variant: 'detailCard' },
      style: ({ theme }) => ({
        padding: theme.spacing(2.5),
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        height: '100%',
        boxSizing: 'border-box',
      }),
    },
    {
      // A card that opens in place to show more of what it already names. Unlike
      // `detailCard` it is stacked rather than gridded, so it takes the width it
      // is given and the height its content asks for; the interactive affordance
      // sits on the header inside it, not on the card, because the open card's
      // body holds text a reader will want to select.
      props: { variant: 'expandableCard' },
      style: ({ theme }) => ({
        padding: theme.spacing(spacing.none),
        borderRadius: borderRadius.card,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        transition: 'border-color 0.2s ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
        },
      }),
    },
    {
      props: { variant: 'selectMenu' },
      style: ({ theme }) => ({
        padding: theme.spacing(spacing.xs, spacing.none),
        marginTop: theme.spacing(spacing.sm),
        boxShadow: theme.shadows[2],
      }),
    },
    {
      // A menu holding actions rather than a list to choose from. Tighter than
      // `selectMenu` and inset by nothing at all: the vertical padding that reads
      // as breathing room around a list would read as a frame around one action.
      // The radius matches the items' so the shadow follows the curve the border
      // does, and the clip catches whatever the two round differently.
      props: { variant: 'actionMenu' },
      style: ({ theme }) => ({
        padding: theme.spacing(spacing.none),
        marginTop: theme.spacing(spacing.xs),
        borderRadius: borderRadius.chip,
        boxShadow: theme.shadows[2],
        overflow: 'hidden',
      }),
    },
    {
      // One choice in a set of mutually exclusive ones, holding a badge icon and a
      // label. Distinct from `orgCard`, whose muted-icon and icon-on-hover rules
      // are for a row that navigates somewhere — here the icon is a brand badge
      // that must stay legible against the blue circle in both modes and must not
      // shift on hover, and the card has to show which one is currently chosen.
      //
      // Selection is read from `aria-pressed`, so the styling cannot drift from
      // what assistive technology is told.
      props: { variant: 'tierCard' },
      style: ({ theme }) => ({
        padding: theme.spacing(2.5),
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        width: '100%',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
        // Idle, the badge is a quiet grey so the unchosen packages do not compete
        // with the chosen one. It only changes on selection, never on hover.
        '& .MuiAvatar-root': {
          backgroundColor: theme.palette.action.selected,
          transition: 'background-color 0.2s ease',
        },
        '& .MuiAvatar-root .MuiSvgIcon-root': {
          color: theme.palette.text.disabled,
          transition: 'color 0.2s ease',
        },
        // A package the selected one includes: the badge stays grey, but its icon
        // takes the brand colour so the covered packages read as part of the
        // choice rather than as excluded. Set only on packages strictly below the
        // selected one, so this never competes with the selected rule below —
        // the two carry equal specificity and would otherwise fall to source order.
        '&[data-included="true"] .MuiAvatar-root .MuiSvgIcon-root': {
          color: theme.palette.primary.main,
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
        '&:hover': {
          borderColor: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
        },
        '&[aria-pressed="true"]': {
          borderColor: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          // A second inner ring, so the chosen card reads as selected rather than
          // merely hovered.
          boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
          // The badge lights up in the brand blue, which is what carries the
          // selection at a glance down a column of otherwise grey cards.
          '& .MuiAvatar-root': {
            backgroundColor: theme.palette.primary.main,
          },
          '& .MuiAvatar-root .MuiSvgIcon-root': {
            color: theme.palette.common.white,
          },
        },
      }),
    },
    {
      props: { variant: 'orgCard' },
      style: ({ theme }) => ({
        padding: theme.spacing(2.5),
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        width: '100%',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
        '& .MuiSvgIcon-root': {
          color: theme.palette.text.disabled,
          transition: 'transform 0.2s ease, color 0.2s ease',
          flexShrink: 0,
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          '& .MuiAvatar-root': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
          },
          '& .OrgCard-name': {
            color: theme.palette.primary.main,
          },
          '& .MuiSvgIcon-root': {
            transform: 'translateX(4px)',
            color: theme.palette.primary.main,
          },
        },
      }),
    },
  ],
}
